import { z } from "zod";
import { centsFromText, isoDateFromText } from "./invoiceImport";
import { parseCsv } from "./customers";

/* Bankseite: Kontoumsätze importieren (CSV oder CAMT) und Rechnungen oder Ausgaben zuordnen. Beträge in ganzen Cent, Eingänge positiv. */

export const bankTransactionSchema = z.object({
  bookingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein Datum angeben."),
  amountCents: z.number().int().refine((n) => n !== 0, "Der Betrag darf nicht 0 sein.").refine((n) => Math.abs(n) <= 1_000_000_000_00),
  counterparty: z.string().trim().max(200).nullable().optional(),
  purpose: z.string().trim().max(1000).nullable().optional(),
  iban: z.string().trim().max(40).nullable().optional(),
});
export type BankTransactionInput = z.input<typeof bankTransactionSchema>;

export const bankImportSchema = z.object({
  transactions: z.array(bankTransactionSchema).min(1, "Es gibt nichts zu importieren.").max(5000, "Bitte höchstens 5000 Umsätze auf einmal."),
});

export const bankMatchSchema = z
  .object({ documentId: z.uuid().optional(), expenseId: z.uuid().optional() })
  .refine((v) => !!v.documentId !== !!v.expenseId, { message: "Bitte wählen Sie eine Rechnung oder eine Ausgabe." });

export interface BankSuggestion {
  kind: "invoice" | "expense";
  id: string;
  label: string;
  /** Warum der Vorschlag passt: Rechnungsnummer im Verwendungszweck oder gleicher Betrag. */
  reason: string;
}

export interface BankTransactionInfo {
  id: string;
  bookingDate: string;
  amountCents: number;
  counterparty: string | null;
  purpose: string | null;
  iban: string | null;
  state: "open" | "matched" | "ignored";
  matchedLabel: string | null;
  suggestion: BankSuggestion | null;
}

export interface BankListResponse {
  items: BankTransactionInfo[];
  openCount: number;
  incomeCents: number;
  outgoCents: number;
}

export interface BankImportResult {
  created: number;
  duplicates: number;
}

type Field = "date" | "amount" | "debit" | "credit" | "counterparty" | "purpose" | "iban";
const HEADER_WORDS: Record<Field, string[]> = {
  date: ["buchungstag", "buchungsdatum", "buchung", "datum", "valuta", "wertstellung", "booking date"],
  amount: ["betrag", "umsatz", "betrag (eur)", "betrag in eur", "amount"],
  debit: ["soll", "belastung"],
  credit: ["haben", "gutschrift"],
  counterparty: ["name zahlungsbeteiligter", "beguenstigter/zahlungspflichtiger", "begünstigter/zahlungspflichtiger", "auftraggeber/empfänger", "empfänger", "auftraggeber", "zahlungspflichtiger", "name", "beguenstigter", "begünstigter", "gegenkonto name"],
  purpose: ["verwendungszweck", "buchungstext", "zweck", "verwendungszweck/buchungstext", "beschreibung"],
  iban: ["iban zahlungsbeteiligter", "iban", "kontonummer/iban", "gegenkonto iban", "iban auftraggeber/empfänger"],
};

export interface CsvBankResult {
  transactions: BankTransactionInput[];
  skipped: { line: number; reason: string }[];
  missing: string[];
}

/** Kontoauszug als CSV: Kopfzeile wird gesucht (Banken schreiben oft Vorspann davor); Datum- und Betragsspalten werden großzügig erkannt. */
export function bankFromCsv(text: string): CsvBankResult {
  const rows = parseCsv(text);
  const result: CsvBankResult = { transactions: [], skipped: [], missing: [] };
  const norm = (s: string) => s.trim().toLowerCase().replace(/^"|"$/g, "");
  const hasWord = (row: string[], f: Field) => row.some((c) => HEADER_WORDS[f].includes(norm(c)));
  const headerAt = rows.findIndex((r) => hasWord(r, "date") && (hasWord(r, "amount") || (hasWord(r, "debit") && hasWord(r, "credit"))));
  if (headerAt < 0) {
    result.missing.push("Spalten für Datum und Betrag");
    return result;
  }
  const header = rows[headerAt]!;
  const index: Partial<Record<Field, number>> = {};
  header.forEach((h, i) => {
    const f = (Object.keys(HEADER_WORDS) as Field[]).find((k) => index[k] === undefined && HEADER_WORDS[k].includes(norm(h)));
    if (f) index[f] = i;
  });
  const cell = (r: string[], f: Field) => (index[f] === undefined ? "" : (r[index[f]!] ?? "").trim());
  rows.slice(headerAt + 1).forEach((r, i) => {
    const line = headerAt + i + 2;
    const date = isoDateFromText(cell(r, "date"));
    if (!date) return void result.skipped.push({ line, reason: "Datum nicht lesbar" });
    let cents: number | null;
    if (index.amount !== undefined) cents = centsFromText(cell(r, "amount"));
    else {
      const d = centsFromText(cell(r, "debit"));
      const c = centsFromText(cell(r, "credit"));
      cents = c ? Math.abs(c) : d ? -Math.abs(d) : null;
    }
    if (!cents) return void result.skipped.push({ line, reason: "Betrag fehlt" });
    result.transactions.push({ bookingDate: date, amountCents: cents, counterparty: cell(r, "counterparty") || null, purpose: cell(r, "purpose") || null, iban: cell(r, "iban") || null });
  });
  return result;
}
